import React, { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Grid,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Select,
    FormControl,
    InputLabel,
    Divider,
} from "@material-ui/core";
import { config as appConfig } from "../../config";
import { getFirstAndLastNames } from "../../utils/Functions";
import { tagPresets } from "../../config/tagPresets";
import {
    getTagConfig,
    saveTagConfig,
    exportTagConfig,
    importTagConfig,
    clampQrBounds,
    SECOND_QR_FIXED_VALUE,
} from "../../utils/tagConfig";
import { toast } from "react-toastify";

const dummyPerson = {
    id: "0001",
    name: "Fulano de Tal",
    company: "Empresa Exemplo S.A.",
    role: "Diretor Executivo",
};

const TagEditor = ({ eventId = 1, person, dialogPrintOpen, handleClosePrint }) => {
    const componentRef = useRef();
    const activePerson = person && person.id ? person : dummyPerson;

    // Estados do layout da etiqueta
    const [presetName, setPresetName] = useState("");
    const [widthMm, setWidthMm] = useState(90);
    const [heightMm, setHeightMm] = useState(50);

    const [showQr, setShowQr] = useState(true);
    const [qrCodePosition, setQrCodePosition] = useState({ top: 14, left: 2 });
    const [qrCodeSizeMm, setQrCodeSizeMm] = useState(20);

    // Segundo QR Code opcional (fixo: "#anm2026")
    const [showSecondQr, setShowSecondQr] = useState(false);
    const [secondQrSide, setSecondQrSide] = useState("right");
    const [secondQrCodePosition, setSecondQrCodePosition] = useState({ top: 14, left: 2 });
    const [secondQrCodeSizeMm, setSecondQrCodeSizeMm] = useState(20);

    const [showName, setShowName] = useState(true);
    const [namePosition, setNamePosition] = useState({ top: 14, left: 24 });
    const [nameFontSize, setNameFontSize] = useState(15);

    const [showCompany, setShowCompany] = useState(true);
    const [companyPosition, setCompanyPosition] = useState({ top: 24, left: 24 });
    const [companyFontSize, setCompanyFontSize] = useState(12);

    const [showRole, setShowRole] = useState(true);
    const [roleFontSize, setRoleFontSize] = useState(14);

    // Novos campos de layout
    const [qrSide, setQrSide] = useState("left");
    const [textAreaLeft, setTextAreaLeft] = useState(24);
    const [textAreaTop, setTextAreaTop] = useState(12);
    const [textAreaSpacing, setTextAreaSpacing] = useState(1);
    const [itemOrder, setItemOrder] = useState(["role", "name", "company"]);
    // Estilos por campo
    const [nameUppercase, setNameUppercase] = useState(true);
    const [nameItalic, setNameItalic] = useState(false);
    const [companyUppercase, setCompanyUppercase] = useState(false);
    const [companyItalic, setCompanyItalic] = useState(false);
    const [roleUppercase, setRoleUppercase] = useState(false);
    const [roleItalic, setRoleItalic] = useState(false);

    // Estado do painel de importação/exportação JSON
    const [showJsonPanel, setShowJsonPanel] = useState(false);
    const [jsonInput, setJsonInput] = useState("");

    // Carregar a configuração ativa ao abrir o modal
    useEffect(() => {
        if (dialogPrintOpen && eventId) {
            const activeConfig = getTagConfig(eventId);
            applyConfigToStates(activeConfig);
            setPresetName(""); // Resetar a seleção de preset
            setShowJsonPanel(false);
            setJsonInput("");
        }
    }, [dialogPrintOpen, eventId]);

    // Aplica um objeto de configuração aos estados do React
    const applyConfigToStates = (cfg) => {
        if (!cfg) return;
        const w = cfg.widthMm ?? 90;
        const h = cfg.heightMm ?? 50;
        setWidthMm(w);
        setHeightMm(h);

        setShowQr(cfg.showQr ?? true);
        setQrCodePosition(cfg.qrCodePosition ?? { top: 14, left: 2 });
        setQrCodeSizeMm(cfg.qrCodeSizeMm ?? 20);

        setShowSecondQr(Boolean(cfg.showSecondQr));
        setSecondQrSide(cfg.secondQrSide ?? "right");
        setSecondQrCodePosition(cfg.secondQrCodePosition ?? { top: 14, left: 2 });
        setSecondQrCodeSizeMm(cfg.secondQrCodeSizeMm ?? 20);

        setShowName(cfg.showName ?? true);
        setNamePosition(cfg.namePosition ?? { top: 14, left: 24 });
        setNameFontSize(cfg.nameFontSize ?? 15);

        setShowCompany(cfg.showCompany ?? true);
        setCompanyPosition(cfg.companyPosition ?? { top: 24, left: 24 });
        setCompanyFontSize(cfg.companyFontSize ?? 15);

        setShowRole(cfg.showRole ?? true);
        setRoleFontSize(cfg.roleFontSize ?? 15);

        setQrSide(cfg.qrSide ?? "left");
        setTextAreaLeft(Number(cfg.textAreaLeft ?? 24));
        setTextAreaTop(Number(cfg.textAreaTop ?? 12));
        setTextAreaSpacing(Number(cfg.textAreaSpacing ?? 1));
        setItemOrder(Array.isArray(cfg.itemOrder) ? cfg.itemOrder : ["role", "name", "company"]);
        setNameUppercase(cfg.nameUppercase !== false);
        setNameItalic(cfg.nameItalic === true);
        setCompanyUppercase(cfg.companyUppercase === true);
        setCompanyItalic(cfg.companyItalic === true);
        setRoleUppercase(cfg.roleUppercase === true);
        setRoleItalic(cfg.roleItalic === true);
    };

    // Limites seguros calculados para os dois QR Codes dentro da tag física
    const qr1Bounds = clampQrBounds(widthMm, heightMm, qrCodeSizeMm, qrCodePosition, qrSide);
    const qr2Bounds = clampQrBounds(widthMm, heightMm, secondQrCodeSizeMm, secondQrCodePosition, secondQrSide);

    // Espaço reservado à direita e à esquerda para a área de texto respeitar os limites físicos e os QR Codes
    const computedRightMarginMm = (() => {
        let rightMm = 2;
        if (showQr && qrSide === "right") {
            rightMm = Math.max(rightMm, qr1Bounds.sizeMm + qr1Bounds.offsetMm + 2);
        }
        if (showSecondQr && secondQrSide === "right") {
            rightMm = Math.max(rightMm, qr2Bounds.sizeMm + qr2Bounds.offsetMm + 2);
        }
        return Math.min(rightMm, Math.max(2, Number(widthMm) - 10));
    })();

    const computedLeftMarginMm = (() => {
        let leftMm = Math.max(0, Number(textAreaLeft) || 0);
        if (showSecondQr && secondQrSide === "left") {
            leftMm = Math.max(leftMm, qr2Bounds.leftMm + qr2Bounds.sizeMm + 2);
        }
        return Math.min(leftMm, Math.max(0, Number(widthMm) - computedRightMarginMm - 10));
    })();

    const computedTopMarginMm = Math.max(0, Math.min(Number(textAreaTop) || 0, Math.max(0, Number(heightMm) - 5)));

    // Retorna a configuração atual agregada (já respeitando limites físicos)
    const getCurrentConfig = () => {
        return {
            widthMm: Number(widthMm),
            heightMm: Number(heightMm),
            showQr,
            qrCodePosition: { top: qr1Bounds.topMm, left: qr1Bounds.offsetMm },
            qrCodeSizeMm: qr1Bounds.sizeMm,
            showSecondQr,
            secondQrSide,
            secondQrCodePosition: { top: qr2Bounds.topMm, left: qr2Bounds.offsetMm },
            secondQrCodeSizeMm: qr2Bounds.sizeMm,
            secondQrValue: SECOND_QR_FIXED_VALUE,
            showName,
            namePosition: { top: Number(namePosition.top), left: Number(namePosition.left) },
            nameFontSize: Number(nameFontSize),
            nameUppercase,
            nameItalic,
            showCompany,
            companyPosition: { top: Number(companyPosition.top), left: Number(companyPosition.left) },
            companyFontSize: Number(companyFontSize),
            companyUppercase,
            companyItalic,
            showRole,
            roleFontSize: Number(roleFontSize),
            roleUppercase,
            roleItalic,
            qrSide,
            textAreaLeft: Number(textAreaLeft),
            textAreaTop: Number(textAreaTop),
            textAreaSpacing: Number(textAreaSpacing),
            itemOrder: [...itemOrder],
        };
    };

    // Manipulador de mudança de preset
    const handleSelectPreset = (e) => {
        const val = e.target.value;
        setPresetName(val);

        if (tagPresets.printers[val]) {
            applyConfigToStates(tagPresets.printers[val]);
            toast.info(`Gabarito "${tagPresets.printers[val].name}" aplicado!`);
        }
    };

    // Ação de Salvar Configuração
    const handleSave = () => {
        const currentCfg = getCurrentConfig();
        const success = saveTagConfig(eventId, currentCfg);
        if (success) {
            toast.success("Configuração do layout da etiqueta salva com sucesso!");
            handleClosePrint();
        } else {
            toast.error("Erro ao salvar configuração.");
        }
    };

    // Ação de Imprimir
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onAfterPrint: () => {
            toast.success("Envio para impressora realizado!");
        },
        pageStyle: `
            @page {
                size: ${widthMm}mm ${heightMm}mm;
                margin: 0;
            }
            body {
                margin: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
            }
            @media print {
                .printable-tag {
                    border: none !important;
                    box-shadow: none !important;
                    background: white !important;
                    margin: 0 !important;
                }
            }
        `,
    });

    // Funções de Importação e Exportação JSON
    const handleExportJson = () => {
        const currentCfg = getCurrentConfig();
        const jsonStr = exportTagConfig(currentCfg);
        setJsonInput(jsonStr);
        setShowJsonPanel(true);

        // Copiar para o clipboard automaticamente
        try {
            navigator.clipboard.writeText(jsonStr);
            toast.success("Código de configuração copiado para a área de transferência!");
        } catch (err) {
            toast.info("Configuração gerada abaixo. Copie o texto manualmente.");
        }
    };

    const handleImportJson = () => {
        if (!jsonInput.trim()) {
            toast.warn("Por favor, cole um código JSON válido antes de importar.");
            return;
        }

        const imported = importTagConfig(jsonInput);
        if (imported) {
            applyConfigToStates(imported);
            setPresetName("");
            setShowJsonPanel(false);
            toast.success("Layout importado com sucesso! Clique em Salvar para fixar.");
        } else {
            toast.error("JSON inválido. Certifique-se de colar o formato correto exportado pelo sistema.");
        }
    };

    return (
        <Dialog onClose={handleClosePrint} open={dialogPrintOpen} maxWidth="md" fullWidth>
            <DialogContent style={{ padding: "24px" }}>
                <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Configuração da Tag de Impressão - Evento #{eventId}</span>
                    <span style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}>
                        Visualização técnica
                    </span>
                </h3>

                <Grid container spacing={3}>
                    {/* Painel Esquerdo: Formulário de Configuração */}
                    <Grid item xs={12} md={6}>
                        {/* Seletor de Presets */}
                        <FormControl variant="outlined" fullWidth size="small" style={{ marginBottom: "20px" }}>
                            <InputLabel id="preset-select-label">Gabarito de Impressora</InputLabel>
                            <Select
                                labelId="preset-select-label"
                                value={presetName}
                                onChange={handleSelectPreset}
                                label="Gabarito de Impressora"
                            >
                                <MenuItem value="">
                                    <em>-- Nenhum preset selecionado --</em>
                                </MenuItem>
                                {Object.entries(tagPresets.printers).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Dimensões Gerais */}
                        <h4 style={{ margin: "10px 0" }}>Dimensões Físicas (mm)</h4>
                        <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Largura (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={widthMm}
                                    onChange={(e) => setWidthMm(Number(e.target.value))}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Altura (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={heightMm}
                                    onChange={(e) => setHeightMm(Number(e.target.value))}
                                    type="number"
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração do QR Code Principal */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: "5px 0" }}>QR Code (Participante)</h4>
                            <FormControlLabel
                                control={<Checkbox checked={showQr} onChange={(e) => setShowQr(e.target.checked)} color="primary" />}
                                label="Exibir"
                            />
                        </div>
                        {showQr && (
                            <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                                {/* Lado do QR Code */}
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel id="qr-side-label">Posição do QR Code</InputLabel>
                                        <Select
                                            labelId="qr-side-label"
                                            value={qrSide}
                                            onChange={(e) => {
                                                const side = e.target.value;
                                                setQrSide(side);
                                                // Ajustar textAreaLeft automaticamente
                                                if (side === "left") {
                                                    setTextAreaLeft(Math.max(qrCodeSizeMm + 4, textAreaLeft));
                                                } else if (!showSecondQr || secondQrSide !== "left") {
                                                    setTextAreaLeft(3);
                                                }
                                            }}
                                            label="Posição do QR Code"
                                        >
                                            <MenuItem value="left">⬅ Esquerda</MenuItem>
                                            <MenuItem value="right">Direita ➡</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodePosition.top}
                                        onChange={(e) => {
                                            const maxTop = Math.max(0, heightMm - qr1Bounds.sizeMm);
                                            const val = Math.max(0, Math.min(Number(e.target.value), maxTop));
                                            setQrCodePosition({ ...qrCodePosition, top: val });
                                        }}
                                        type="number"
                                        inputProps={{ min: 0, max: Math.max(0, heightMm - qr1Bounds.sizeMm) }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label={qrSide === "right" ? "Margem Dir (mm)" : "Left (mm)"}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodePosition.left}
                                        onChange={(e) => {
                                            const maxLeft = Math.max(0, widthMm - qr1Bounds.sizeMm);
                                            const val = Math.max(0, Math.min(Number(e.target.value), maxLeft));
                                            setQrCodePosition({ ...qrCodePosition, left: val });
                                        }}
                                        type="number"
                                        inputProps={{ min: 0, max: Math.max(0, widthMm - qr1Bounds.sizeMm) }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Tamanho (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={qrCodeSizeMm}
                                        onChange={(e) => {
                                            const maxSz = Math.min(widthMm, heightMm);
                                            const val = Math.max(5, Math.min(Number(e.target.value), maxSz));
                                            setQrCodeSizeMm(val);
                                        }}
                                        type="number"
                                        inputProps={{ min: 5, max: Math.min(widthMm, heightMm) }}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Configuração do 2º QR Code Opcional (Fixo: #anm2026) */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <h4 style={{ margin: "5px 0 2px" }}>2º QR Code (Fixo: {SECOND_QR_FIXED_VALUE})</h4>
                                <span style={{ fontSize: 11, color: "#777" }}>
                                    Opcional — limitado automaticamente às bordas da tag ({widthMm}x{heightMm}mm)
                                </span>
                            </div>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showSecondQr}
                                        onChange={(e) => setShowSecondQr(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Exibir"
                            />
                        </div>
                        {showSecondQr && (
                            <Grid container spacing={2} style={{ marginBottom: "15px", marginTop: "4px" }}>
                                <Grid item xs={6}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel id="second-qr-side-label">Posição do 2º QR Code</InputLabel>
                                        <Select
                                            labelId="second-qr-side-label"
                                            value={secondQrSide}
                                            onChange={(e) => setSecondQrSide(e.target.value)}
                                            label="Posição do 2º QR Code"
                                        >
                                            <MenuItem value="left">⬅ Esquerda</MenuItem>
                                            <MenuItem value="right">Direita ➡</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Código Fixo"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={SECOND_QR_FIXED_VALUE}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Top (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={secondQrCodePosition.top}
                                        onChange={(e) => {
                                            const maxTop = Math.max(0, heightMm - qr2Bounds.sizeMm);
                                            const val = Math.max(0, Math.min(Number(e.target.value), maxTop));
                                            setSecondQrCodePosition({ ...secondQrCodePosition, top: val });
                                        }}
                                        type="number"
                                        inputProps={{ min: 0, max: Math.max(0, heightMm - qr2Bounds.sizeMm) }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label={secondQrSide === "right" ? "Margem Dir (mm)" : "Left (mm)"}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={secondQrCodePosition.left}
                                        onChange={(e) => {
                                            const maxLeft = Math.max(0, widthMm - qr2Bounds.sizeMm);
                                            const val = Math.max(0, Math.min(Number(e.target.value), maxLeft));
                                            setSecondQrCodePosition({ ...secondQrCodePosition, left: val });
                                        }}
                                        type="number"
                                        inputProps={{ min: 0, max: Math.max(0, widthMm - qr2Bounds.sizeMm) }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Tamanho (mm)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={secondQrCodeSizeMm}
                                        onChange={(e) => {
                                            const maxSz = Math.min(widthMm, heightMm);
                                            const val = Math.max(5, Math.min(Number(e.target.value), maxSz));
                                            setSecondQrCodeSizeMm(val);
                                        }}
                                        type="number"
                                        inputProps={{ min: 5, max: Math.min(widthMm, heightMm) }}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Coluna de Texto: posição e espaçamento */}
                        <h4 style={{ margin: "5px 0 10px" }}>Área de Texto</h4>
                        <Grid container spacing={2} style={{ marginBottom: "15px" }}>
                            <Grid item xs={4}>
                                <TextField
                                    label="Left (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={textAreaLeft}
                                    onChange={(e) => setTextAreaLeft(Number(e.target.value))}
                                    type="number"
                                    helperText="início horizontal"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Top (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={textAreaTop}
                                    onChange={(e) => setTextAreaTop(Number(e.target.value))}
                                    type="number"
                                    helperText="início vertical"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Espaço (mm)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={textAreaSpacing}
                                    onChange={(e) => setTextAreaSpacing(Number(e.target.value))}
                                    type="number"
                                    helperText="entre linhas"
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ margin: "15px 0" }} />

                        {/* Ordem e visibilidade dos campos */}
                        <h4 style={{ margin: "5px 0 10px" }}>Campos e Ordem de Exibição</h4>
                        <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                            Use ▲ ▼ para reordenar os itens na etiqueta
                        </div>
                        {itemOrder.map((field, idx) => {
                            const labels = { role: "Titulo", name: "Nome", company: "Empresa" };
                            const showStates = { name: showName, company: showCompany, role: showRole };
                            const fontSizes = { name: nameFontSize, company: companyFontSize, role: roleFontSize };
                            const fontSetters = { name: setNameFontSize, company: setCompanyFontSize, role: setRoleFontSize };
                            const showSetters = { name: setShowName, company: setShowCompany, role: setShowRole };
                            const uppercaseStates = { name: nameUppercase, company: companyUppercase, role: roleUppercase };
                            const uppercaseSetters = { name: setNameUppercase, company: setCompanyUppercase, role: setRoleUppercase };
                            const italicStates = { name: nameItalic, company: companyItalic, role: roleItalic };
                            const italicSetters = { name: setNameItalic, company: setCompanyItalic, role: setRoleItalic };

                            const moveUp = (i) => {
                                if (i === 0) return;
                                const newOrder = [...itemOrder];
                                [newOrder[i - 1], newOrder[i]] = [newOrder[i], newOrder[i - 1]];
                                setItemOrder(newOrder);
                            };
                            const moveDown = (i) => {
                                if (i === itemOrder.length - 1) return;
                                const newOrder = [...itemOrder];
                                [newOrder[i], newOrder[i + 1]] = [newOrder[i + 1], newOrder[i]];
                                setItemOrder(newOrder);
                            };

                            const btnStyle = (active) => ({
                                border: `1px solid ${active ? "#1976d2" : "#bdbdbd"}`,
                                borderRadius: 4,
                                background: active ? "#1976d2" : "#fff",
                                color: active ? "#fff" : "#555",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "2px 7px",
                                lineHeight: "1.4",
                                transition: "all 0.15s",
                            });

                            return (
                                <div
                                    key={field}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 8,
                                        padding: "8px 10px",
                                        marginBottom: 6,
                                        background: showStates[field] ? "#e8f5e9" : "#f5f5f5",
                                        borderRadius: 6,
                                        border: `1px solid ${showStates[field] ? "#a5d6a7" : "#e0e0e0"}`,
                                    }}
                                >
                                    {/* Botões de ordem */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                        <button
                                            onClick={() => moveUp(idx)}
                                            disabled={idx === 0}
                                            style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", fontSize: 12, lineHeight: 1, color: idx === 0 ? "#ccc" : "#555", padding: "1px 4px" }}
                                            title="Mover para cima"
                                        >▲</button>
                                        <button
                                            onClick={() => moveDown(idx)}
                                            disabled={idx === itemOrder.length - 1}
                                            style={{ background: "none", border: "none", cursor: idx === itemOrder.length - 1 ? "default" : "pointer", fontSize: 12, lineHeight: 1, color: idx === itemOrder.length - 1 ? "#ccc" : "#555", padding: "1px 4px" }}
                                            title="Mover para baixo"
                                        >▼</button>
                                    </div>

                                    {/* Label */}
                                    <span style={{ fontWeight: 600, minWidth: 55, fontSize: 13 }}>{labels[field]}</span>

                                    {/* Fonte */}
                                    <TextField
                                        label="Fonte (pt)"
                                        variant="outlined"
                                        size="small"
                                        value={fontSizes[field]}
                                        onChange={(e) => fontSetters[field](Number(e.target.value))}
                                        type="number"
                                        style={{ width: 85 }}
                                        disabled={!showStates[field]}
                                    />

                                    {/* Caixa Alta */}
                                    <button
                                        onClick={() => uppercaseSetters[field](!uppercaseStates[field])}
                                        style={btnStyle(uppercaseStates[field])}
                                        title="Caixa alta (uppercase)"
                                        disabled={!showStates[field]}
                                    >AA</button>

                                    {/* Itálico */}
                                    <button
                                        onClick={() => italicSetters[field](!italicStates[field])}
                                        style={{ ...btnStyle(italicStates[field]), fontStyle: "italic" }}
                                        title="Itálico"
                                        disabled={!showStates[field]}
                                    ><em>I</em></button>

                                    {/* Exibir toggle */}
                                    <FormControlLabel
                                        style={{ marginLeft: "auto", marginRight: 0 }}
                                        control={
                                            <Checkbox
                                                checked={showStates[field]}
                                                onChange={(e) => showSetters[field](e.target.checked)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={<span style={{ fontSize: 12 }}>Exibir</span>}
                                    />
                                </div>
                            );
                        })}
                    </Grid>

                    {/* Painel Direito: Preview e Importação/Exportação */}
                    <Grid item xs={12} md={6} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <h4 style={{ alignSelf: "flex-start", marginTop: 0 }}>Visualização da Credencial (Escala Fisiológica)</h4>

                        {/* Área de Preview Física */}
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                background: "#eceff1",
                                padding: "20px",
                                borderRadius: "8px",
                                minHeight: "260px",
                                boxSizing: "border-box",
                                marginBottom: "20px",
                            }}
                        >
                            <div
                                ref={componentRef}
                                className="printable-tag"
                                style={{
                                    width: `${widthMm}mm`,
                                    height: `${heightMm}mm`,
                                    position: "relative",
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                    backgroundColor: "#ffffff",
                                    border: "1px dashed #78909c",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                                    transition: "width 0.2s, height 0.2s",
                                }}
                            >
                                {/* QR Code Principal — respeitando limites da tag */}
                                {showQr && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${qr1Bounds.topMm}mm`,
                                            left: `${qr1Bounds.leftMm}mm`,
                                            width: `${qr1Bounds.sizeMm}mm`,
                                            height: `${qr1Bounds.sizeMm}mm`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img
                                            src={`${appConfig.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${encodeURIComponent(activePerson.id)}`}
                                            alt="QR Code"
                                            style={{ width: "100%", height: "100%" }}
                                        />
                                    </div>
                                )}

                                {/* 2º QR Code Opcional (#anm2026) — respeitando limites físicos e de impressão */}
                                {showSecondQr && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: `${qr2Bounds.topMm}mm`,
                                            left: `${qr2Bounds.leftMm}mm`,
                                            width: `${qr2Bounds.sizeMm}mm`,
                                            height: `${qr2Bounds.sizeMm}mm`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img
                                            src={`${appConfig.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${encodeURIComponent(SECOND_QR_FIXED_VALUE)}`}
                                            alt={`QR Code ${SECOND_QR_FIXED_VALUE}`}
                                            style={{ width: "100%", height: "100%" }}
                                        />
                                    </div>
                                )}

                                {/* Coluna de texto — flex vertical com itemOrder e contida nos limites físicos */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: `${computedTopMarginMm}mm`,
                                        left: `${computedLeftMarginMm}mm`,
                                        right: `${computedRightMarginMm}mm`,
                                        maxHeight: `${Math.max(0, heightMm - computedTopMarginMm)}mm`,
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {itemOrder.map((field) => {
                                        if (field === "role" && showRole && activePerson.role) {
                                            return (
                                                <div key="role" style={{ fontSize: `${roleFontSize}pt`, fontStyle: roleItalic ? "italic" : "normal", fontWeight: "600", fontFamily: "'Inter','Roboto',sans-serif", color: "#546e7a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "1.2", marginBottom: `${textAreaSpacing * 0.4}mm`, textTransform: roleUppercase ? "uppercase" : "none" }}>
                                                    {activePerson.role}
                                                </div>
                                            );
                                        }
                                        if (field === "name" && showName) {
                                            return (
                                                <div key="name" style={{ fontSize: `${nameFontSize}pt`, fontWeight: "700", fontStyle: nameItalic ? "italic" : "normal", fontFamily: "'Inter','Roboto',sans-serif", color: "#000", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word", lineHeight: "1.2", marginBottom: `${textAreaSpacing * 0.4}mm`, textTransform: nameUppercase ? "uppercase" : "none" }}>
                                                    {activePerson.name ? getFirstAndLastNames(activePerson.name) : ""}
                                                </div>
                                            );
                                        }
                                        if (field === "company" && showCompany && activePerson.company) {
                                            return (
                                                <div key="company" style={{ fontSize: `${companyFontSize}pt`, fontWeight: "600", fontStyle: companyItalic ? "italic" : "normal", fontFamily: "'Inter','Roboto',sans-serif", color: "#37474f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "1.2", marginBottom: `${textAreaSpacing * 0.4}mm`, textTransform: companyUppercase ? "uppercase" : "none" }}>
                                                    {activePerson.company}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Ações Técnicas: Import/Export */}
                        <div style={{ width: "100%", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                color="default"
                                onClick={handleExportJson}
                            >
                                Copiar/Exportar Configuração
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                color="default"
                                onClick={() => {
                                    setJsonInput("");
                                    setShowJsonPanel(!showJsonPanel);
                                }}
                            >
                                Importar
                            </Button>
                        </div>

                        {/* Painel JSON */}
                        {showJsonPanel && (
                            <div style={{ width: "100%", marginTop: "15px" }}>
                                <TextField
                                    label="Configuração JSON"
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    fullWidth
                                    value={jsonInput}
                                    onChange={(e) => setJsonInput(e.target.value)}
                                    placeholder="Cole o JSON de configuração exportado aqui..."
                                    style={{ fontFamily: "monospace", fontSize: "12px", marginBottom: "10px" }}
                                />
                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        onClick={() => setShowJsonPanel(false)}
                                    >
                                        Fechar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleImportJson}
                                    >
                                        Aplicar JSON
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions style={{ padding: "16px 24px", background: "#f5f5f5" }}>
                <Button
                    variant="contained"
                    onClick={handleClosePrint}
                    style={{ background: "#9e9e9e", color: "white" }}
                >
                    Fechar
                </Button>

                {/* Botão de Teste de Impressão imediato */}
                <Button
                    variant="contained"
                    onClick={handlePrint}
                    style={{ background: "#ff9800", color: "white" }}
                >
                    Testar Impressão
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    color="primary"
                >
                    Salvar Como Padrão
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TagEditor;
